import Router from 'next/router';
import { useState, useEffect } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Stack, Button, Typography, Fab, Grid, FormControlLabel, Checkbox } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import dayjs from 'dayjs';
import buddhistEraPlugin from 'dayjs/plugin/buddhistEra';
import customParseFormatPlugin from 'dayjs/plugin/customParseFormat';
import { useTranslation } from 'next-i18next';
import { useSnackbar } from 'notistack';
import { useForm, FormProvider, useFormContext, Controller } from 'react-hook-form';
import Cookies from 'universal-cookie';

import { Container, FormErrorText, LinkText, CheckboxLabel, AcceptPolicyBox, useStyles } from '../style';

import LoadingButton from '@components/atoms/loadingButton';
import EnrollmentContentSteps from '@components/molecules/cpdEnrollment/enrollmentContentSteps';
import cpdAPI from 'src/api/cpd';
import { CPDEnrollmentForm, CPDEnrollmentFieldNames } from 'src/helpers/constants';
import { useMediaQueryDevice, useProfile } from 'src/helpers/use';
import { toFormatCPDEnrollmentStringDate } from 'src/helpers/utils';
import {
  ICPDCourseEnrollment,
  ICPDProfile,
  ICPDCourse,
  CPDRegulatorTypeEnum,
  LicenseRenewalTypeEnum,
  IEnrollmentStep,
} from 'src/interfaces';

type Props = {
  handleBack: () => void;
  activeStep: IEnrollmentStep;
  steps: IEnrollmentStep[];
  lastIndex: number;
};

const ProductCPDEnrollmentFooter = ({ handleBack, activeStep, steps, lastIndex }: Props) => {
  const {
    watch,
    control,
    formState: { errors },
  } = useFormContext();
  const { matchesMobile } = useMediaQueryDevice();
  const { t } = useTranslation();
  const errorMessage = errors && errors[CPDEnrollmentFieldNames.isAcceptConsentCPDTermAndPolicy]?.message;

  const classes = useStyles();

  return (
    <>
      {lastIndex === activeStep.id && (
        <AcceptPolicyBox className={errorMessage ? 'error' : ''}>
          <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%', p: { xs: 2 } }}>
            <Grid item xs={12} container justifyContent="center">
              <Controller
                rules={{
                  required: {
                    value: true,
                    message: t('cpd.policy.please_accept_policy'),
                  },
                  validate: (value) => !!value,
                }}
                name={CPDEnrollmentFieldNames.isAcceptConsentCPDTermAndPolicy}
                control={control}
                defaultValue={false}
                render={({ field, fieldState: { error } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        size="small"
                        {...field}
                        sx={{ color: error ? 'errorDark.main' : undefined, alignSelf: 'baseline' }}
                      />
                    }
                    classes={{ label: classes.label }}
                    label={
                      <CheckboxLabel>
                        <span>{t('cpd.policy.part_1')}</span>
                        <LinkText href="/cpd/cpd-privacy-policy" target="_blank" rel="noreferrer">
                          {t('cpd.policy.part_2')}
                        </LinkText>{' '}
                        <span>{t('cpd.policy.part_3')}</span>
                        <LinkText href="/cpd/cpd-terms" target="_blank" rel="noreferrer">
                          {t('cpd.policy.part_4')}
                        </LinkText>
                      </CheckboxLabel>
                    }
                  />
                )}
              />
            </Grid>
            {errorMessage && (
              <Grid
                item
                xs={12}
                container
                justifyContent="center"
                alignItems="center"
                sx={{ ml: { xs: 4 }, mr: { xs: 2 }, mt: { xs: 1, md: '-4px', lg: 0 } }}
              >
                <FormErrorText>{errorMessage}</FormErrorText>
              </Grid>
            )}
          </Grid>
        </AcceptPolicyBox>
      )}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          mt: lastIndex === activeStep.id ? 'initial' : 'auto',
          bgcolor: '#F5F5F5',
          height: { md: '100px', xs: '70px' },
          width: '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Container>
          <Stack direction="row" justifyContent="space-between">
            {activeStep.id === 0 ? (
              <Box />
            ) : (
              <Button className="btn back" variant={matchesMobile ? 'text' : 'outlined'} onClick={handleBack}>
                {t('go_back')}
              </Button>
            )}
            <LoadingButton
              loading={watch(CPDEnrollmentFieldNames.loading)}
              variant="contained"
              className="btn"
              type="submit"
            >
              {lastIndex === activeStep.id ? t('proceed_to_payment') : t('next')}
            </LoadingButton>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default ProductCPDEnrollmentFooter;
